import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import { Topbar, Footer, Sidebar } from './components';
import { useTranslation } from 'react-i18next';
import Loader from 'components/organisms/Loader';

const useStyles = makeStyles((theme) => ({
  root: {
    // height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '500px',
  },
  main: {
    flex: 1,
  },
  loadingEffect: {
    display: 'none',
  },
}));

const Main = ({ children, themeToggler, themeMode }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const prevRouteRef = useRef();

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const history = useHistory();
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    setTimeoutHandler(1000);
  }, []);

  useEffect(() => {
    history.listen((location) => {
      if (prevRouteRef.current !== location.pathname) {
        if (location.pathname !== '/financial-statistics' || location.pathname !== '/financial-indicators')
          setTimeoutHandler(1000);
      }
      prevRouteRef.current = location.pathname;
    });
  }, [history]);

  const setTimeoutHandler = (time) => {
    setShowLoader(true);
    setTimeout(() => {
      setShowLoader(false);
    }, time || 1000);
  };

  const pages = {
    home: {
      title: t('HOME_PAGE.HOME'),
      id: 'home',
      href: '/',
    },
    legislation: {
      title: t('HOME_PAGE.LEGISLATION'),
      id: 'legislation',
      href: '/legislation',
    },
    budget: {
      title: t('HOME_PAGE.BUDGET'),
      id: 'budget',
      href: '/budget',
    },
    reports: {
      title: t('HOME_PAGE.REPORTS'),
      id: 'reports',
      href: '/reports',
    },
    statistics: {
      multiple: true,
      title: t('HOME_PAGE.STATISTICS'),
      id: 'statistics',
      href: 'statistics',
      subpages: [
        {
          multiple: true,
          title: t('HOME_PAGE.FINANCIAL_INDICATORS'),
          id: 'financial-indicators',
          href: 'financial-indicators',
          subpages: [
            {
              title: t('HOME_PAGE.NATIONAL_INDICATORS'),
              id: 'national-indicators',
              href: '/national-indicators',
            },
            {
              title: t('HOME_PAGE.LOCAL_INDICATORS'),
              id: 'local-indicators',
              href: '/local-indicators',
            },
          ]
        },
        {
          title: t('HOME_PAGE.FINANCIAL_STATISTICS'),
          id: 'financial-statistics',
          href: '/financial-statistics',
        },
      ],
    },
    latestNews: {
      title: t('HOME_PAGE.LATEST_NEWS'),
      id: 'latest-news',
      href: '/latest-news',
    },
  };

  const [openSidebar, setOpenSidebar] = useState(false);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };

  const open = isMd ? false : openSidebar;

  return (
    <div
      className={clsx({
        [classes.root]: true,
      })}
    >
      <Topbar onSidebarOpen={handleSidebarOpen} pages={pages} themeMode={themeMode} themeToggler={themeToggler} />
      <Sidebar onClose={handleSidebarClose} open={open} variant='temporary' pages={pages} />
      {showLoader && <Loader />}
      <main className={clsx(classes.main, showLoader && classes.loadingEffect)}>{children}</main>
      <Footer pages={pages} />
    </div>
  );
};

Main.propTypes = {
  children: PropTypes.node,
  themeToggler: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
};

export default Main;
